import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  Undo,
  Redo,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const COLOR_SWATCHES = [
  { name: "White", value: "#ffffff" },
  { name: "Yellow", value: "#f5c14b" },
  { name: "Red", value: "#ef4444" },
  { name: "Green", value: "#22c55e" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Grey", value: "#9ca3af" },
];

function ToolBtn({ active, onClick, title, children, theme = "dark", "data-testid": testId }) {
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors flex-shrink-0 ${
        active
          ? dark
            ? "bg-paul-yellow/20 text-paul-yellow"
            : "bg-paul-yellow/30 text-navy"
          : dark
            ? "text-white/70 hover:bg-white/10 hover:text-white"
            : "text-gray-500 hover:bg-gray-100 hover:text-navy"
      }`}
    >
      {children}
    </button>
  );
}

function Divider({ theme }) {
  return (
    <div
      className={`w-px h-5 self-center mx-1 ${theme === "dark" ? "bg-white/10" : "bg-gray-200"}`}
    />
  );
}

/**
 * RichTextEditor — TipTap-powered editor with a Notion-style toolbar.
 *
 * Props:
 *  - value: HTML string
 *  - onChange: (html) => void
 *  - placeholder: shown when empty (CSS-driven)
 *  - theme: "dark" | "light"
 *  - minHeight: CSS string (default 120px)
 *  - testIdPrefix
 */
export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write a post…",
  theme = "dark",
  minHeight = "120px",
  testIdPrefix = "rte",
}) {
  const dark = theme === "dark";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "underline",
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap returns "<p></p>" for empty content — normalize to "".
      onChange?.(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        "data-testid": `${testIdPrefix}-content`,
        class: `tiptap-content focus:outline-none px-3 py-2 ${
          dark ? "text-white" : "text-navy"
        }`,
        style: `min-height:${minHeight}`,
      },
    },
  });

  const addLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("URL:", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const safe = url.startsWith("http") || url.startsWith("mailto:") ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      data-testid={`${testIdPrefix}-root`}
      className={`rounded-xl border overflow-hidden ${
        dark ? "border-white/10 bg-[#0b0f1e]" : "border-gray-200 bg-white"
      }`}
    >
      {/* Toolbar */}
      <div
        className={`flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b ${
          dark ? "border-white/10 bg-[#10162a]" : "border-gray-200 bg-gray-50"
        }`}
      >
        <ToolBtn theme={theme} title="Bold" active={editor.isActive("bold")}
          data-testid={`${testIdPrefix}-bold`}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldIcon className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Italic" active={editor.isActive("italic")}
          data-testid={`${testIdPrefix}-italic`}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicIcon className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Underline" active={editor.isActive("underline")}
          data-testid={`${testIdPrefix}-underline`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="w-4 h-4" />
        </ToolBtn>

        <Divider theme={theme} />

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Text color"
              data-testid={`${testIdPrefix}-color`}
              className={`h-8 w-8 rounded-md flex items-center justify-center transition-colors flex-shrink-0 ${
                dark ? "text-white/70 hover:bg-white/10" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              <Palette className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className={`w-auto p-2 grid grid-cols-6 gap-1.5 ${dark ? "bg-[#10162a] border-white/10" : "bg-white"}`}
          >
            {COLOR_SWATCHES.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.name}
                data-testid={`${testIdPrefix}-color-${c.name.toLowerCase()}`}
                onClick={() => editor.chain().focus().setColor(c.value).run()}
                className="w-6 h-6 rounded-full border border-white/20"
                style={{ background: c.value }}
              />
            ))}
            <button
              type="button"
              title="Clear color"
              data-testid={`${testIdPrefix}-color-clear`}
              onClick={() => editor.chain().focus().unsetColor().run()}
              className={`col-span-6 mt-1 text-[11px] py-1 rounded ${dark ? "text-white/60 hover:bg-white/10" : "text-gray-500 hover:bg-gray-100"}`}
            >
              Clear color
            </button>
          </PopoverContent>
        </Popover>

        <Divider theme={theme} />

        <ToolBtn theme={theme} title="Heading 1" active={editor.isActive("heading", { level: 1 })}
          data-testid={`${testIdPrefix}-h1`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Heading 2" active={editor.isActive("heading", { level: 2 })}
          data-testid={`${testIdPrefix}-h2`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Heading 3" active={editor.isActive("heading", { level: 3 })}
          data-testid={`${testIdPrefix}-h3`}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="w-4 h-4" />
        </ToolBtn>

        <Divider theme={theme} />

        <ToolBtn theme={theme} title="Bullet list" active={editor.isActive("bulletList")}
          data-testid={`${testIdPrefix}-ul`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Numbered list" active={editor.isActive("orderedList")}
          data-testid={`${testIdPrefix}-ol`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Quote" active={editor.isActive("blockquote")}
          data-testid={`${testIdPrefix}-quote`}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="w-4 h-4" />
        </ToolBtn>

        <Divider theme={theme} />

        <ToolBtn theme={theme} title="Align left" active={editor.isActive({ textAlign: "left" })}
          data-testid={`${testIdPrefix}-align-left`}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeft className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Align center" active={editor.isActive({ textAlign: "center" })}
          data-testid={`${testIdPrefix}-align-center`}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenter className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Align right" active={editor.isActive({ textAlign: "right" })}
          data-testid={`${testIdPrefix}-align-right`}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRight className="w-4 h-4" />
        </ToolBtn>

        <Divider theme={theme} />

        <ToolBtn theme={theme} title="Link" active={editor.isActive("link")}
          data-testid={`${testIdPrefix}-link`}
          onClick={addLink}>
          <LinkIcon className="w-4 h-4" />
        </ToolBtn>

        <Divider theme={theme} />

        <ToolBtn theme={theme} title="Undo"
          data-testid={`${testIdPrefix}-undo`}
          onClick={() => editor.chain().focus().undo().run()}>
          <Undo className="w-4 h-4" />
        </ToolBtn>
        <ToolBtn theme={theme} title="Redo"
          data-testid={`${testIdPrefix}-redo`}
          onClick={() => editor.chain().focus().redo().run()}>
          <Redo className="w-4 h-4" />
        </ToolBtn>
      </div>

      {/* Editor */}
      <EditorContent
        editor={editor}
        className="tiptap-shell"
        data-placeholder={placeholder}
      />
    </div>
  );
}
